// src/components/Locations/PhotoModerationTab.js
// Staff photo moderation — view and manage photos uploaded by family members
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, Alert, CircularProgress, Stack,
  Card, CardMedia, CardContent, Grid, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, MenuItem, TextField
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Favorite as LikedIcon,
  Block as HiddenIcon,
  ZoomIn as ZoomInIcon,
  Photo as PhotoIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { familyLinksApi } from '../../services/apiClient';
import { useThemeSettings } from '../../contexts/ThemeContext';

const PhotoModerationTab = ({ locationId }) => {
  const { t } = useThemeSettings();
  const { enqueueSnackbar } = useSnackbar();

  const [links, setLinks] = useState([]);
  const [allPhotos, setAllPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterLink, setFilterLink] = useState('all');

  // Zoom dialog
  const [zoomPhoto, setZoomPhoto] = useState(null);

  const fetchPhotos = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    setError(null);
    try {
      // First fetch all links for this location
      const linksResponse = await familyLinksApi.getAll({ locationId });
      const locationLinks = linksResponse.data?.data || [];
      setLinks(locationLinks);

      // Then fetch photos for each link that has photos
      const linksWithPhotos = locationLinks.filter(l => l.photosUploaded > 0);
      const photoPromises = linksWithPhotos.map(async (link) => {
        try {
          const photosResponse = await familyLinksApi.getPhotos(link.id, { limit: 100 });
          return (photosResponse.data?.data || []).map(p => ({
            ...p,
            linkId: link.id,
            familyName: link.familyName,
            residentName: link.residentName,
            personId: link.personId,
          }));
        } catch {
          return [];
        }
      });

      const photoArrays = await Promise.all(photoPromises);
      const combined = photoArrays.flat().sort((a, b) =>
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      setAllPhotos(combined);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const filteredPhotos = filterLink === 'all'
    ? allPhotos
    : allPhotos.filter(p => p.linkId === parseInt(filterLink));

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric'
    }).format(new Date(dateString));
  };

  const getReactionBadge = (reaction) => {
    if (reaction === 'liked') return <Chip icon={<LikedIcon />} label="Liked" size="small" color="success" />;
    if (reaction === 'disliked') return <Chip icon={<HiddenIcon />} label="Hidden" size="small" color="error" />;
    return <Chip label="Neutral" size="small" variant="outlined" />;
  };

  const photoStats = {
    total: allPhotos.length,
    liked: allPhotos.filter(p => p.reaction === 'liked').length,
    disliked: allPhotos.filter(p => p.reaction === 'disliked').length,
    neutral: allPhotos.filter(p => p.reaction === 'neutral').length,
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Photo Moderation
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchPhotos}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Stats bar */}
      {allPhotos.length > 0 && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Chip icon={<PhotoIcon />} label={`${photoStats.total} total`} variant="outlined" />
          <Chip icon={<LikedIcon />} label={`${photoStats.liked} liked`} color="success" variant="outlined" />
          <Chip icon={<HiddenIcon />} label={`${photoStats.disliked} hidden`} color="error" variant="outlined" />
          <Chip label={`${photoStats.neutral} neutral`} variant="outlined" />
        </Stack>
      )}

      {/* Filter by family link */}
      {links.length > 1 && (
        <TextField
          select
          label="Filter by family member"
          value={filterLink}
          onChange={(e) => setFilterLink(e.target.value)}
          size="small"
          sx={{ mb: 2, minWidth: 250 }}
        >
          <MenuItem value="all">All family members</MenuItem>
          {links.filter(l => l.photosUploaded > 0).map((link) => (
            <MenuItem key={link.id} value={link.id}>
              {link.familyName || 'Unknown'} — {link.residentName} ({link.photosUploaded} photos)
            </MenuItem>
          ))}
        </TextField>
      )}

      {loading && <CircularProgress size={24} sx={{ display: 'block', mx: 'auto', my: 3 }} />}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && allPhotos.length === 0 && !error && (
        <Alert severity="info">
          No photos have been uploaded by family members yet.
        </Alert>
      )}

      {/* Photo grid */}
      {filteredPhotos.length > 0 && (
        <Grid container spacing={2}>
          {filteredPhotos.map((photo) => (
            <Grid item xs={6} sm={4} md={3} key={photo.id}>
              <Card
                variant="outlined"
                sx={{
                  '&:hover': { boxShadow: 2 },
                  transition: 'box-shadow 0.2s',
                  opacity: photo.reaction === 'disliked' ? 0.6 : 1,
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    image={photo.thumbnailUrl || photo.url}
                    alt={photo.description || 'Family photo'}
                    sx={{
                      aspectRatio: '1',
                      objectFit: 'cover',
                      cursor: 'pointer',
                      bgcolor: 'grey.200',
                    }}
                    onClick={() => setZoomPhoto(photo)}
                  />
                  {/* Zoom icon overlay */}
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                    }}
                    onClick={() => setZoomPhoto(photo)}
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Box>
                <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                  <Typography variant="caption" fontWeight={500} display="block" noWrap>
                    {photo.uploaderName || photo.familyName || 'Unknown'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    For: {photo.residentName || 'Unknown'}
                  </Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(photo.createdAt)}
                    </Typography>
                    {getReactionBadge(photo.reaction)}
                  </Stack>
                  {photo.description && (
                    <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ mt: 0.5 }}>
                      {photo.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Zoom Dialog */}
      <Dialog
        open={!!zoomPhoto}
        onClose={() => setZoomPhoto(null)}
        maxWidth="md"
        fullWidth
      >
        {zoomPhoto && (
          <>
            <DialogTitle sx={{ pb: 0 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle1">
                    {zoomPhoto.description || 'Photo'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Uploaded by {zoomPhoto.uploaderName || zoomPhoto.familyName || 'Unknown'} for {zoomPhoto.residentName}
                    {' \u2014 '}{formatDate(zoomPhoto.createdAt)}
                  </Typography>
                </Box>
                {getReactionBadge(zoomPhoto.reaction)}
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Box
                component="img"
                src={zoomPhoto.url || zoomPhoto.thumbnailUrl}
                alt={zoomPhoto.description || 'Photo'}
                sx={{
                  width: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 1,
                  mt: 1,
                }}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                {zoomPhoto.viewCount != null && (
                  <Chip label={`Shown ${zoomPhoto.viewCount} times`} size="small" variant="outlined" />
                )}
                {zoomPhoto.isVisible === false && (
                  <Chip label="Hidden from screensaver" size="small" color="warning" />
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setZoomPhoto(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PhotoModerationTab;
